import SearchableSelect from '../common/SearchableSelect';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Container, Form, Spinner } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { makeApiRequests, ABORT_ERROR } from '../../helpers/api';
import { addDays, dayLabel, daysFor, eventOnDay, moveAnchor, timeRange, todayKey } from './calendarDates';
import './store-calendar.css';
import PersonalCalendarButtons from './PersonalCalendarButtons';
import BookingDialog from './BookingDialog';
import { calendarLink, calendarTarget } from './bookingNavigation';

const ROOT = '/store-calendar';
const statusClass = status => status.toLowerCase().replace(/[^a-z]/g, '');
const emptyFilters = { team: '', person: '', teamLabel: '', personLabel: '' };


export default function StoreCalendar() {
  const location = useLocation();
  const history = useHistory();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [metaVersion, setMetaVersion] = useState(0);
  const [storeId, setStoreId] = useState('');
  const target = calendarTarget(location.search);
  const [anchor, setAnchor] = useState(() => target.day || todayKey());
  const [view, setView] = useState('week');
  const [filters, setFilters] = useState(emptyFilters);
  const { team, person } = filters;
  const [staff, setStaff] = useState({ storeId: '', people: [], teams: [], error: '', loading: false });
  const [showCancelled, setShowCancelled] = useState(false);
  const [list, setList] = useState({ key: '', events: [], error: '', loadedAt: null });
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [notice, setNotice] = useState('');
  const [savedEvent, setSavedEvent] = useState(null);
  const [contactError, setContactError] = useState('');
  const days = daysFor(anchor, view);
  const start = days[0];
  const end = addDays(days[days.length - 1], 1);
  const listKey = `${storeId}:${start}:${end}:${team}:${person}`;
  const selectedStore = meta && meta.stores.find(s => s.id === storeId);
  const contactParam = new URLSearchParams(location.search).get('contact');

  useEffect(() => {
    const controller = new AbortController();
    setMetaError('');
    makeApiRequests({ endpoint: `${ROOT}/meta`, method: 'GET', signal: controller.signal }).then(result => {
      if (controller.signal.aborted) return;
      if (result.error) { setMetaError(result.error); return; }
      if (!result.response || !Array.isArray(result.response.stores)) { setMetaError('Could not load your store calendars.'); return; }
      setMeta(result.response);
      const firstStoreId = result.response.stores.length ? result.response.stores[0].id : '';
      const requestedStore = result.response.stores.find(s => s.id === target.storeId);
      setStoreId(old => requestedStore ? requestedStore.id : result.response.stores.some(s => s.id === old) ? old : firstStoreId);
    });
    return () => controller.abort();
  }, [metaVersion, target.storeId]);

  useEffect(() => { if (target.day) setAnchor(target.day); }, [target.day]);

  useEffect(() => {
    if (!meta || !contactParam) return undefined;
    const controller = new AbortController();
    setContactError('');
    makeApiRequests({ endpoint: `${ROOT}/contacts/${encodeURIComponent(contactParam)}`, method: 'GET', signal: controller.signal }).then(result => {
      if (controller.signal.aborted) return;
      if (result.error) { setContactError(result.error); return; }
      const contact = result.response && result.response.contact;
      if (!contact || !meta.stores.some(s => s.id === contact.storeId)) { setContactError('Customer calendar is unavailable.'); return; }
      setStoreId(contact.storeId); setFilters(emptyFilters);
      setBooking({ contact, day: todayKey() });
    });
    return () => controller.abort();
  }, [meta, contactParam]);

  useEffect(() => {
    if (!storeId) return undefined;
    const controller = new AbortController();
    setStaff(old => old.storeId === storeId ? { ...old, loading: true, error: '' }
      : { storeId, people: [], teams: [], error: '', loading: true });
    makeApiRequests({ endpoint: `${ROOT}/staff?store=${storeId}`, method: 'GET', signal: controller.signal }).then(result => {
      if (controller.signal.aborted) return;
      const data = result.response;
      if (result.error || !data || data.storeId !== storeId || !Array.isArray(data.people) || !Array.isArray(data.teams)) {
        setStaff(old => ({ ...old, loading: false, error: result.error || 'Could not load staff filters.' }));
      } else setStaff({ ...data, error: '', loading: false });
    });
    return () => controller.abort();
  }, [storeId, refreshVersion]);

  useEffect(() => {
    if (!storeId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    const query = new URLSearchParams({ store: storeId, start, end });
    if (team) query.set('team', team);
    if (person) query.set('salesperson', person);
    makeApiRequests({ endpoint: `${ROOT}/events?${query}`, method: 'GET', signal: controller.signal }).then(result => {
      if (controller.signal.aborted) return;
      setLoading(false);
      if (result.error && result.error !== ABORT_ERROR) {
        setList(old => ({ key: listKey, events: old.key === listKey ? old.events : [], loadedAt: old.key === listKey ? old.loadedAt : null, error: result.error }));
      } else if (!result.response || !Array.isArray(result.response.events)) {
        setList({ key: listKey, events: [], loadedAt: null, error: 'Could not load appointments.' });
      } else setList({ key: listKey, events: result.response.events, loadedAt: new Date(), error: '' });
    });
    return () => controller.abort();
  }, [storeId, start, end, team, person, listKey, refreshVersion]);

  const refresh = useCallback(() => setRefreshVersion(v => v + 1), []);
  useEffect(() => {
    const visibleRefresh = () => { if (!document.hidden) refresh(); };
    const timer = setInterval(visibleRefresh, 30000);
    window.addEventListener('focus', visibleRefresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', visibleRefresh); };
  }, [refresh]);

  function closeBooking() {
    setBooking(null);
    if (contactParam) history.replace(calendarLink(storeId, anchor));
  }
  function saved(event) {
    closeBooking(); setAnchor(event.startLocal.slice(0, 10)); setFilters(emptyFilters);
    if (event.status === 'Cancelled') setShowCancelled(true);
    setNotice(event.status === 'Cancelled' ? 'Appointment cancelled.' : 'Appointment saved to the shared store calendar.');
    setSavedEvent({ event, store: selectedStore });
    refresh();
  }
  const events = list.key === listKey ? list.events : [];
  const storeStaff = staff.storeId === storeId ? staff : { people: [], teams: [], error: '', loading: true };
  const selectedTeam = storeStaff.teams.find(t => t.id === team);
  const peopleMap = new Map(storeStaff.people.map(p => [p.id, p.name + (p.enabled === false ? ' (inactive)' : '')]));
  // Keep historical appointment owners selectable, even after their staff record moves.
  if (!team) events.forEach(e => { if (e.salespersonId && !peopleMap.has(e.salespersonId)) peopleMap.set(e.salespersonId, e.salesperson); });
  const people = Array.from(peopleMap.entries()).filter(([id]) => !team || (selectedTeam && selectedTeam.memberIds.includes(id)))
    .sort((a, b) => a[1].localeCompare(b[1]));
  const filtered = events.filter(e => showCancelled || e.status !== 'Cancelled');
  const listError = list.key === listKey && list.error;
  const isLoaded = list.key === listKey && list.loadedAt;
  const title = view === 'month' ? dayLabel(anchor, { month: 'long', year: 'numeric' })
    : `${dayLabel(start, { month: 'short', day: 'numeric' })} – ${dayLabel(days[6], { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const eventButton = event => <button type="button" key={event.id} className={`store-calendar-event status-${statusClass(event.status)}`}
    onClick={() => { setNotice(''); setBooking({ event }); }}>
    <strong>{timeRange(event)}</strong><span>{event.customer}</span><small>{event.salesperson} · {event.status}</small>
  </button>;

  return (
    <Container fluid className="store-calendar py-3 px-md-4">
      <div className="store-calendar-heading"><div><h1>Store Calendar</h1><p className="text-muted">See who’s coming in and who they’re meeting. All times are Central Time.</p></div>
        <Button onClick={() => { setNotice(''); setBooking({ day: anchor }); }} disabled={!selectedStore}>Book Appointment</Button>
      </div>
      {metaError && <Alert variant="danger">{metaError} <Button variant="link" onClick={() => setMetaVersion(v => v + 1)}>Try again</Button></Alert>}
      {!meta && !metaError && <p role="status"><Spinner as="span" size="sm" animation="border" /> Loading store calendars…</p>}
      {meta && !meta.stores.length && <Alert variant="info">Your account has no store calendar assigned. Ask an administrator to set your sales location.</Alert>}
      {contactError && <Alert variant="danger">{contactError} <Link to="/crm/calendar">Return to calendar</Link></Alert>}
      {notice && <Alert variant="success" role="status" dismissible onClose={() => setNotice('')}>{notice}
        {savedEvent && <PersonalCalendarButtons event={savedEvent.event} store={savedEvent.store} />}
      </Alert>}
      {selectedStore && <>
        <div className="store-calendar-controls">
          <Form.Group controlId="calendar-store"><Form.Label>Sales location</Form.Label><SearchableSelect inputId="calendar-store" value={storeId}
            options={meta.stores.map(s => ({ value: s.id, label: s.name }))} isClearable={false}
            onChange={id => { setStoreId(id); setFilters(emptyFilters); setNotice(''); }} /></Form.Group>
          <Form.Group controlId="calendar-team"><Form.Label>Sales manager / team</Form.Label><SearchableSelect inputId="calendar-team" value={team}
            isDisabled={storeStaff.loading && !storeStaff.teams.length}
            options={[{ value: '', label: 'Whole store' }, ...(team && !selectedTeam ? [{ value: team, label: (filters.teamLabel || 'Selected team') + ' (unavailable)' }] : []),
              ...storeStaff.teams.map(t => ({ value: t.id, label: t.name + ' — team' }))]}
            onChange={id => setFilters({ ...emptyFilters, team: id, teamLabel: (storeStaff.teams.find(t => t.id === id) || {}).name || '' })} /></Form.Group>
          <Form.Group controlId="calendar-person"><Form.Label>Sales rep</Form.Label><SearchableSelect inputId="calendar-person" value={person}
            isDisabled={storeStaff.loading && !people.length}
            options={[{ value: '', label: team ? 'Everyone on this team' : 'All salespeople' },
              ...(person && !people.some(([id]) => id === person) ? [{ value: person, label: filters.personLabel || 'Selected salesperson' }] : []),
              ...people.map(([id, name]) => ({ value: id, label: name }))]}
            noOptionsMessage={() => team ? 'No matching rep on this team. Clear staff filters to search the store.' : 'No matching salesperson at this location.'}
            onChange={id => setFilters(old => ({ ...old, person: id, personLabel: peopleMap.get(id) || '' }))} /></Form.Group>
          <Form.Group controlId="calendar-view"><Form.Label>View</Form.Label><Form.Control as="select" value={view} onChange={e => setView(e.target.value)}>
            <option value="week">Week</option><option value="month">Month</option><option value="agenda">Agenda</option>
          </Form.Control></Form.Group>
          <Form.Group controlId="calendar-date"><Form.Label>Go to date</Form.Label><Form.Control type="date" value={anchor} min="2000-01-01" max="2099-12-31" onChange={e => { if (/^20\d{2}-\d{2}-\d{2}$/.test(e.target.value)) setAnchor(e.target.value); }} /></Form.Group>
        </div>
        {storeStaff.error && <Alert variant="danger">{storeStaff.error} <Button variant="link" onClick={refresh}>Retry staff filters</Button></Alert>}
        <div className="store-calendar-filter-summary">
          <span>{team ? `${filters.teamLabel || 'Selected manager'}’s team includes the manager and assigned salespeople at this location.` : 'Teams follow the salespeople assigned to each manager in the CRM.'}</span>
          {(team || person) && <Button variant="link" size="sm" onClick={() => setFilters(emptyFilters)}>Clear staff filters</Button>}
        </div>
        <div className="store-calendar-toolbar">
          <div className="store-calendar-pager"><Button variant="outline-secondary" aria-label="Previous period" onClick={() => setAnchor(moveAnchor(anchor, view, -1))}>‹</Button>
            <Button variant="outline-secondary" onClick={() => setAnchor(todayKey())}>Today</Button>
            <Button variant="outline-secondary" aria-label="Next period" onClick={() => setAnchor(moveAnchor(anchor, view, 1))}>›</Button>
          </div><h2>{title}</h2><Button variant="outline-primary" onClick={refresh} disabled={loading}>Refresh</Button>
        </div>
        <div className="store-calendar-sync"><Form.Check id="calendar-cancelled" label="Show cancelled" checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)} />
          <span role="status">{loading ? 'Refreshing…' : isLoaded ? 'Refreshes every 30 seconds while open' : 'Loading appointments…'}</span>
        </div>
        {listError && <Alert variant="danger">{listError}{isLoaded && ' Shown appointments may be out of date.'} <Button variant="link" onClick={refresh}>Try again</Button></Alert>}
        {isLoaded && !filtered.length && !listError && <Alert variant="info">{person ? 'No appointments for this salesperson in this period.' : team ? 'No appointments for this manager’s team in this period.' : 'No appointments in this period yet. Book a visit to add it to your store’s shared calendar.'}</Alert>}
        <div className={view === 'agenda' ? 'store-calendar-grid-hidden' : 'store-calendar-grid'}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="store-calendar-weekday">{day}</div>)}
          {days.map(day => <section key={day} aria-label={dayLabel(day, { dateStyle: 'full' })} className={`store-calendar-day ${day === todayKey() ? 'is-today' : ''} ${view === 'month' && day.slice(0, 7) !== anchor.slice(0, 7) ? 'is-outside' : ''}`}>
            <div className="store-calendar-day-top"><strong>{dayLabel(day, { month: 'short', day: 'numeric' })}</strong><button type="button" aria-label={`Book Appointment on ${day}`} onClick={() => setBooking({ day })}>+</button></div>
            {filtered.filter(e => eventOnDay(e, day)).map(eventButton)}
          </section>)}
        </div>
        <div className={view === 'agenda' ? 'store-calendar-agenda' : 'store-calendar-agenda store-calendar-mobile'}>
          {days.filter(day => filtered.some(e => eventOnDay(e, day))).map(day => <section key={day}><h3>{dayLabel(day, { weekday: 'long', month: 'long', day: 'numeric' })}</h3>{filtered.filter(e => eventOnDay(e, day)).map(eventButton)}</section>)}
        </div>
        <p className="text-muted mt-3">Use <strong>Book Appointment</strong> to schedule a visit. Changing a contact’s pipeline status alone does not reserve a time.</p>
      </>}
      {booking && selectedStore && <BookingDialog booking={booking} store={selectedStore} statuses={meta.statuses} onClose={closeBooking} onSaved={saved} />}
    </Container>
  );
}
